/** @typedef {'gold'|'purple'} BaseType */
/** @typedef {'200ml'|'250ml'|'300ml'|'500ml'|'1L'} SizeId */
/** @typedef {'plain'|'custom'} ProductType */
/** @typedef {'jar'|'pouch'} PackagingType */

/**
 * Optional flat uplift on purple vs gold at cart level (currently folded into base matrix).
 */
export const PURPLE_BASE_UPCHARGE = 0;

/** Each powdered botanical add-on on eligible sizes (500ml and 1L only). */
export const BOTANICAL_ADDON_GBP = 1.5;

const MAX_BOTANICALS = 3;

/**
 * Plain sea moss base price only (no botanical add-ons).
 * Purple bases are priced above gold at the same volume and packaging.
 * @param {BaseType} base
 * @param {number} sizeMl 200 | 250 | 300 | 500 | 1000
 * @param {PackagingType} packaging
 * @returns {number}
 */
export function lookupPlainBasePriceGbp(base, sizeMl, packaging) {
  const isPurple = base === "purple";
  const pouch = packaging === "pouch";

  if (sizeMl === 200 || sizeMl === 250) {
    return isPurple ? 6.99 : 5.99;
  }

  if (sizeMl === 300) {
    if (pouch) return isPurple ? 9.99 : 8.99;
    return isPurple ? 11.99 : 10.99;
  }

  if (sizeMl === 500) {
    if (pouch) return isPurple ? 15.99 : 14.99;
    return isPurple ? 17.99 : 16.99;
  }

  if (sizeMl === 1000) {
    if (pouch) return isPurple ? 26.99 : 24.99;
    return isPurple ? 29.99 : 27.99;
  }

  return isPurple ? 17.99 : 16.99;
}

/** Lowest plain gold price (200ml trial pouch). */
export function lowestPlainGoldPriceGbp() {
  return lookupPlainBasePriceGbp("gold", 200, "pouch");
}

/** One custom botanical on top of plain base (display). */
export function customFromOneBotanicalGbp(base, sizeMl, packaging) {
  return lookupPlainBasePriceGbp(base, sizeMl, packaging) + BOTANICAL_ADDON_GBP;
}

/**
 * @param {BaseType} base
 * @param {number} sizeMl
 * @param {PackagingType} packaging
 * @param {boolean} [_isCustom] ignored; kept for backwards compatibility with older call sites
 */
export function lookupLinePriceGbp(base, sizeMl, packaging, _isCustom = false) {
  return lookupPlainBasePriceGbp(base, sizeMl, packaging);
}

/**
 * Eligible sizes: 500ml and 1L (jar or pouch). Trial and 300ml: no add-ons.
 * @param {number} sizeMl
 * @param {number} herbCount
 */
export function botanicalAddonTotalGbp(sizeMl, herbCount) {
  if (sizeMl < 500 || sizeMl > 1000) return 0;
  const n = Math.min(Math.max(0, herbCount), MAX_BOTANICALS);
  return n * BOTANICAL_ADDON_GBP;
}

/**
 * Builder price: plain base + £1.50 per selected botanical (max 3) on 500ml / 1L.
 * @param {BaseType} baseType
 * @param {SizeId} sizeId
 * @param {number} herbCount
 * @param {PackagingType} packaging
 */
export function computeBlendPrice(baseType, sizeId, herbCount, packaging = "jar") {
  const ml =
    sizeId === "1L"
      ? 1000
      : sizeId === "250ml"
        ? 250
        : sizeId === "200ml"
          ? 200
          : Number(String(sizeId).replace(/ml$/, "")) || 500;
  let pack = packaging === "pouch" ? "pouch" : "jar";
  if (ml === 200 || ml === 250) pack = "pouch";
  const plainBase = lookupPlainBasePriceGbp(baseType, ml, pack);
  const addon = botanicalAddonTotalGbp(ml, herbCount);
  const uplift = baseType === "purple" ? PURPLE_BASE_UPCHARGE : 0;
  return plainBase + addon + uplift;
}

/**
 * @param {number} sizeMl
 * @returns {SizeId}
 */
export function sizeMlToSizeId(sizeMl) {
  if (sizeMl === 1000) return "1L";
  if (sizeMl === 250) return "250ml";
  if (sizeMl === 200) return "200ml";
  return /** @type {SizeId} */ (`${sizeMl}ml`);
}

/**
 * Cart / checkout reconciliation: plain base + £1.50 per herb (max 3) on 500ml / 1L.
 * @param {{ base_type: BaseType, size_ml: number, product_type: ProductType, herbs?: string[], packaging_type?: PackagingType }} item
 */
export function computeCartLinePrice(item) {
  const herbs = Array.isArray(item.herbs) ? item.herbs : [];
  let sizeMl = typeof item.size_ml === "number" && !Number.isNaN(item.size_ml) ? item.size_ml : 500;

  let packaging = item.packaging_type === "pouch" || item.packaging_type === "jar" ? item.packaging_type : "jar";
  if (sizeMl === 200 || sizeMl === 250) packaging = "pouch";

  const plainBase = lookupPlainBasePriceGbp(item.base_type || "gold", sizeMl, packaging);
  const addon = botanicalAddonTotalGbp(sizeMl, herbs.length);

  const uplift = item.base_type === "purple" ? PURPLE_BASE_UPCHARGE : 0;
  return plainBase + addon + uplift;
}
